'use client'

import type {CSSProperties} from 'react'
import {useTranslation} from '../../../hooks/useTranslation'
import {cn} from '../../../lib/utils'
import {SegmentedTabs} from '../../ui'
import {PublicEntryChromeContext} from './PublicEntryChromeContext'
import {useContext} from 'react'
import type {PublicSitePage} from './publicSiteNavigation'

type PublicOfferTabsProps = {
    activePage: PublicOfferPage
    compactBottomSpacing?: boolean
    showCourses?: boolean
    showEvents: boolean
    showPrivateBookings: boolean
}

export type PublicOfferPage = Extract<PublicSitePage, 'courses' | 'events' | 'privateBookings' | 'schedule'>

const OFFER_TAB_ITEMS = [
    {
        href: '/',
        labelKey: 'publicEntry.offerTabs.group.title',
        page: 'schedule',
    },
    {
        href: '/indywidualne',
        labelKey: 'publicEntry.offerTabs.private.title',
        page: 'privateBookings',
    },
    {
        href: '/wydarzenia',
        labelKey: 'publicEntry.offerTabs.events.title',
        page: 'events',
    },
    {href: '/kursy', labelKey: 'publicEntry.nav.courses', page: 'courses'},
] as const

const STUDIO_THEME_COLORS = {
    '--segmented-tabs-track': 'var(--landing-primary-50)',
    '--segmented-tabs-accent': 'var(--landing-primary-600)',
} as CSSProperties

export function PublicOfferTabs(props: PublicOfferTabsProps) {
    const inherited = useContext(PublicEntryChromeContext)
    return inherited ? null : <StandalonePublicOfferTabs {...props}/>
}

function StandalonePublicOfferTabs({
    activePage,
    compactBottomSpacing = false,
    showCourses = false,
    showEvents,
    showPrivateBookings,
}: PublicOfferTabsProps) {
    const {t} = useTranslation('landing')
    const items = OFFER_TAB_ITEMS.filter((item) => {
        if (item.page === 'courses') return showCourses
        if (item.page === 'events') return showEvents
        if (item.page === 'privateBookings') return showPrivateBookings
        return true
    })

    if (items.length <= 1) return null

    return (
        <nav
            className={cn(
                'relative left-1/2 -mt-7 w-screen -translate-x-1/2 border-b border-[var(--landing-neutral-100)]',
                compactBottomSpacing ? 'mb-0' : 'mb-4 sm:mb-6',
            )}
            aria-label={t('publicEntry.offerTabs.label')}
        >
            <div className="mx-auto flex w-full max-w-2xl justify-center px-4 py-3 sm:px-6" style={STUDIO_THEME_COLORS}>
                <SegmentedTabs
                    activeKey={activePage}
                    items={items.map((item) => ({key: item.page, href: item.href, label: t(item.labelKey)}))}
                    fill={false}
                    indicatorMemoryKey="public-offer-tabs"
                />
            </div>
        </nav>
    )
}
